import { useCallback, useRef, useState } from "react";
import type { FormEvent } from "react";
import { api } from "../api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ModalShell } from "./ui/modal";

type SlugCheckState =
  | { status: "idle" }
  | { status: "checking" }
  | { status: "available" }
  | { status: "unavailable"; message: string }
  | { status: "error"; message: string };

type CreatePageModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: (pageSlug: string) => void;
};

const DEFAULT_UNAVAILABLE_MESSAGE = "Esse endereço não está disponível. Escolha outro.";

function sanitizeSlugInput(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

export function CreatePageModal({ open, onClose, onSuccess }: CreatePageModalProps) {
  const [slug, setSlug] = useState("");
  const [slugCheck, setSlugCheck] = useState<SlugCheckState>({ status: "idle" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentSlugRef = useRef(slug);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update ref when slug changes
  currentSlugRef.current = slug;

  const checkSlugAvailability = useCallback(
    async (slugValue: string): Promise<{ available: boolean; message?: string; stale?: boolean }> => {
      const targetSlug = slugValue.trim();
      if (!targetSlug) {
        setSlugCheck({ status: "idle" });
        return { available: false };
      }

      setSlugCheck({ status: "checking" });
      const res = await api.checkPageSlugAvailability(targetSlug);

      if (currentSlugRef.current !== targetSlug) {
        return { available: false, stale: true };
      }

      if (!res.ok) {
        const fallback = res.error || "Não foi possível verificar a disponibilidade do endereço.";
        setSlugCheck({ status: "error", message: fallback });
        return { available: false, message: fallback };
      }

      if (res.data.available) {
        setSlugCheck({ status: "available" });
        return { available: true };
      }

      const message = res.data.message || DEFAULT_UNAVAILABLE_MESSAGE;
      setSlugCheck({ status: "unavailable", message });
      return { available: false, message };
    },
    []
  );

  const handleSlugChange = (value: string) => {
    const sanitized = sanitizeSlugInput(value);
    setSlug(sanitized);
    setSlugCheck({ status: "idle" });
    setError(null);

    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout for auto-check after 1 second
    if (sanitized.trim()) {
      debounceTimeoutRef.current = setTimeout(() => {
        checkSlugAvailability(sanitized);
      }, 1000);
    }
  };

  const handleSlugBlur = () => {
    if (slug.trim()) {
      checkSlugAvailability(slug);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!slug.trim()) {
      setError("Digite um endereço para sua página.");
      return;
    }

    if (slugCheck.status === "checking") {
      setError("Aguarde a verificação do endereço.");
      return;
    }

    let availability = slugCheck.status === "available" && currentSlugRef.current === slug
      ? { available: true }
      : await checkSlugAvailability(slug);

    if (availability.stale) {
      availability = await checkSlugAvailability(slug);
      if (availability.stale) {
        setError("Não foi possível verificar a disponibilidade do endereço. Tente novamente.");
        return;
      }
    }

    if (!availability.available) {
      setError(availability.message || DEFAULT_UNAVAILABLE_MESSAGE);
      return;
    }

    setLoading(true);

    // Create default page
    const defaultPage = {
      slug: slug.trim(),
      title: "Minha Página de Links",
      title_size: "large" as const,
      description: "Bem-vindo à minha página de links! Aqui você encontra tudo o que precisa saber sobre mim e meus trabalhos.",
      background_color: "#000000",
    };

    const res = await api.savePage(defaultPage);

    if (!res.ok) {
      setError(res.error || "Erro ao criar página.");
      setLoading(false);
      return;
    }

    setLoading(false);
    onSuccess(res.data.slug);
    onClose();

    // Reset form
    setSlug("");
    setSlugCheck({ status: "idle" });
    setError(null);
  };

  const handleClose = () => {
    if (!loading) {
      // Clear any pending timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      setSlug("");
      setSlugCheck({ status: "idle" });
      setError(null);
      onClose();
    }
  };

  return (
    <ModalShell open={open} onClose={handleClose}>
      <div className="bg-white rounded-3xl p-6 shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">Criar nova página</h2>
          <Button
            variant="close"
            onClick={handleClose}
            disabled={loading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Endereço da página *
            </label>
            <div className="flex rounded-lg border border-neutral-200 overflow-hidden">
              <span className="bg-neutral-100 px-3 py-3 text-sm text-neutral-500 hidden sm:inline">
                hublinks.com/
              </span>
              <input
                type="text"
                value={slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                onBlur={handleSlugBlur}
                placeholder="seu-nome"
                className="flex-1 px-3 py-3 border-0 focus:ring-0 focus:outline-none text-sm"
                required
                disabled={loading}
              />
            </div>
            <p className="text-xs text-neutral-500 mt-1">
              Use letras minúsculas, números e hífens. Máximo de 40 caracteres.
            </p>
            {slugCheck.status === "checking" && (
              <p className="text-xs text-neutral-500 mt-1">Verificando disponibilidade...</p>
            )}
            {slugCheck.status === "unavailable" && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 mt-2">
                {slugCheck.message}
              </div>
            )}
            {slugCheck.status === "error" && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-600 mt-2">
                {slugCheck.message}
              </div>
            )}
            {slugCheck.status === "available" && (
              <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-600 mt-2">
                ✅ Endereço disponível!
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              variant="primary"
              disabled={!slug.trim() || loading || slugCheck.status !== "available"}
              className="flex-1"
            >
              {loading ? "Criando..." : "Criar página"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={loading}
            >
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    </ModalShell>
  );
}
